import { useCallback, useEffect, useRef } from "react";
import { DataMappingWebSocket } from "../api/dataMappingWebSocket";
import { useChat } from "../state/useChat";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { createdAnOutputFrame } from "../components/outputFrames";

// How long an app-initiated turn waits for the socket to open before giving up. Generous enough to
// cover a normal handshake landing after a fast upload, short enough that a genuinely dead connection
// does not leave a timer running for the life of the page.
const AUTO_SEND_WAIT_MS = 8000;

function toStr(data) {
  if (data === null || data === undefined) return "";
  if (typeof data === "string") return data;
  if (typeof data === "object" && "output" in data) return String(data.output);
  return JSON.stringify(data);
}

export function useDataMappingWebSocket(sessionId) {
  const wsRef = useRef(null);
  const chat = useChat();
  const workingPanel = useWorkingPanel();

  // Stash latest store snapshots in refs so the message handler stays stable.
  const chatRef = useRef(chat);
  const wpRef = useRef(workingPanel);
  chatRef.current = chat;
  wpRef.current = workingPanel;

  useEffect(() => {
    if (!sessionId) return undefined;

    const handleMessage = (msg) => {
      switch (msg.type) {
        case "thinking":
          chatRef.current.setThinking(true);
          // The turn's real start. Unlike `isThinking`, this stays true until `response` or `error` —
          // see `isTurnActive` in DataMappingContext for why the two cannot be one flag.
          chatRef.current.setTurnActive(true);
          break;
        case "token": {
          chatRef.current.setThinking(false);
          const tokenStr = toStr(msg.data);
          if (!tokenStr) break;
          // Do NOT inspect chatRef.current.messages to decide new-bubble-vs-append. That ref holds the
          // last RENDER's snapshot, and tokens arrive many-per-frame, so every chunk in a burst read the
          // same stale state and started its own bubble — one sentence split across four cards, broken
          // mid-word. The reducer sees current state; let it decide.
          chatRef.current.appendToken(tokenStr);
          break;
        }
        case "response": {
          chatRef.current.setThinking(false);
          chatRef.current.setTurnActive(false);
          chatRef.current.resolveAssistant(toStr(msg.data));
          break;
        }
        case "error":
          chatRef.current.setThinking(false);
          chatRef.current.setTurnActive(false);
          // isError distinguishes a genuine failed turn from routine system
          // notices ("Using tool: ...") — see ChatMessage.jsx, which renders
          // errors distinctly so a crashed turn isn't mistaken for normal chatter.
          chatRef.current.addMessage({
            role: "system",
            content: `Error: ${toStr(msg.data)}`,
            isError: true,
          });
          break;
        // A plain notice, not an error and not a token: the server sends one while it waits for the
        // session's write lock, so a wait of more than a moment says so instead of going silent. Silence is
        // what made the user in the reported case type the message a second time. Deliberately does NOT
        // touch `isThinking` — no turn has started yet, and showing the indicator would promise one.
        case "system":
          chatRef.current.addMessage({
            role: "system",
            content: toStr(msg.data),
          });
          break;
        case "tool_start": {
          // create_chart/delete_chart already narrate themselves via the
          // chart:ref card / the chart disappearing from the board — a
          // second "Using tool: ..." line would be redundant, same reasoning
          // as the existing run_python suppression below.
          if (["run_python", "create_chart", "delete_chart"].includes(msg.data?.tool)) break;
          chatRef.current.addMessage({
            role: "system",
            content: `Using tool: ${msg.data?.tool}...`,
          });
          break;
        }
        case "agent_action": {
          const action = msg.data;
          if (action?.tool === "run_python" && action.code) {
            chatRef.current.addMessage({
              role: "system",
              content: "",
              codeExecution: { code: action.code, output: "" },
            });
          }
          break;
        }
        case "data_modified":
          wpRef.current.triggerRefresh();
          // The entire "charts stay live" mechanism: a chart's series is
          // never stored (see chart_builder.py), only recomputed on read, so
          // refetching here is enough to reflect whatever run_python/etc.
          // just changed — no separate invalidation step exists anywhere.
          wpRef.current.refreshCharts();
          break;
        case "token_usage": {
          const data = msg.data || {};
          chatRef.current.setTokenUsage({
            input: data.input_tokens ?? 0,
            output: data.output_tokens ?? 0,
            cacheRead: data.cache_read_tokens ?? 0,
            cacheWrite: data.cache_write_tokens ?? 0,
          });
          break;
        }
        case "tool_end": {
          const end = msg.data || {};
          if (end.tool === "run_python") {
            chatRef.current.updateCodeExecutionOutput(end.output);
            if (end.output?.includes("DataFrame modified")) {
              wpRef.current.triggerRefresh();
            }
          }
          if (
            end.tool === "create_dataframe" ||
            end.tool === "delete_dataframe" ||
            end.tool === "rename_dataframe"
          ) {
            wpRef.current.triggerRefresh();
          }
          // The agent has just built the frame the user reviews, so move the panel to Map & review rather
          // than leaving them on Working data wondering where the output went. Only on create, and only on
          // a create that actually produced an output frame — cloning a working frame, or a failed call,
          // must not yank the panel to a stage with nothing in it while the agent is still working.
          if (end.tool === "create_dataframe" && createdAnOutputFrame(end.output)) {
            wpRef.current.outputCreated();
          }
          if (end.tool === "create_chart") {
            // Only steal focus to the Charts board on a genuine success — a
            // failed create_chart must not yank the panel to an (empty)
            // board mid-conversation while the agent is still recovering.
            if (!toStr(end.output).startsWith("Error")) {
              wpRef.current.chartCreated();
            } else {
              wpRef.current.refreshCharts();
            }
          }
          if (end.tool === "delete_chart") {
            wpRef.current.refreshCharts();
          }
          break;
        }
        case "agent_finish":
        default:
          break;
      }
    };

    const ws = new DataMappingWebSocket(
      sessionId,
      handleMessage,
      (connected, detail) => {
        chatRef.current.setConnected(connected);
        // Auth and tenancy refusals arrive as a close code, so without this the user is told only
        // "disconnected" and has no idea their sign-in was rejected. `reason` is only set for the cases
        // worth explaining — an ordinary disconnect stays silent.
        if (!connected && detail?.reason) {
          chatRef.current.addMessage({
            role: "system",
            content: detail.reason,
            isError: true,
          });
        }
      }
    );
    ws.connect();
    wsRef.current = ws;

    return () => {
      ws.disconnect();
      wsRef.current = null;
    };
  }, [sessionId]);

  const sendMessage = useCallback(
    (text) => {
      if (wsRef.current) {
        chatRef.current.addMessage({ role: "user", content: text });
        wsRef.current.send(text);
      }
    },
    []
  );

  // A turn the app starts for the user rather than one they typed: the post-upload analysis, the mapping
  // when they open Map & review, and the greeting on a brand-new session.
  //
  // Separate from `sendMessage` rather than a flag on it, because the two differ in every respect that
  // matters: this one renders as a system notice instead of a user bubble, carries a `display` label to the
  // backend, and must fail quietly. Folding them together would mean a boolean that changes three
  // behaviours at once.
  //
  // `display` is doing more work than its name suggests. Its PRESENCE on the wire is the backend's marker
  // for "the app started this turn" (see dataMappingWebSocket.js), which also exempts the turn from the
  // server's lock-wait notice — otherwise a message nobody wrote gets "your message will be sent as soon as
  // it finishes" written about it.
  //
  // The greeting used to have its own near-identical copy of this function, deliberately WITHOUT `display`,
  // so that it would "read as a genuine turn, not a quiet app-initiated one". That reasoning is reversed:
  // sending it as a user turn meant the transcript showed a black "hello" bubble the user never typed, live
  // and again on every reload. It is an app-initiated turn, and saying so is what stops it lying.
  //
  // `onSent` is called only once the send actually goes through, and callers must not mark their own
  // one-shot guards before it. Under StrictMode's dev-mode double-invoke, the connect effect above tears
  // down and recreates `ws` between the two effect runs — so a guard set synchronously spends its only
  // attempt on a socket (`ws1`) that is killed before it ever opens, and the second effect run (against the
  // real, persisting `ws2`) sees the guard already spent and never retries. The greeting silently never
  // sent. That is a real bug that was fixed once; letting the guard flip only on success is the fix.
  const sendAutoMessage = useCallback((text, display, onSent) => {
    const ws = wsRef.current;
    if (!ws || !text) return false;

    const send = () => {
      // Send BEFORE narrating. The socket can close between the isOpen check and here, and an
      // "Analysing…" notice with nothing behind it is worse than silence — it promises a reply that is
      // never coming.
      const sent = ws.send(text, { display, quiet: true });
      if (sent) {
        // Locally the turn is a system notice; on reload the same styling comes back via the persisted
        // `auto` flag on the message (store.append_turn), so the two paths agree.
        chatRef.current.addMessage({ role: "system", content: display || text, auto: true });
        onSent?.();
      }
      return sent;
    };

    if (ws.isOpen) return send();

    // The socket connects on mount and an upload — or a first page load — can land before it opens. `send`
    // would drop the message, so wait for the open event instead, but bounded: there is no reconnect logic
    // in this client, so if it has not opened by now it probably is not going to, and an auto-turn is not
    // worth an indefinite pending timer.
    const started = Date.now();
    const timer = setInterval(() => {
      if (wsRef.current !== ws) {
        clearInterval(timer);           // session changed under us; the prompt is stale
        return;
      }
      if (ws.isOpen) {
        clearInterval(timer);
        send();
        return;
      }
      if (Date.now() - started > AUTO_SEND_WAIT_MS) {
        // Deliberately silent. The user did not write this, so there is nothing for them to retry, and
        // they will already have a connection warning from the socket itself.
        clearInterval(timer);
      }
    }, 150);
    return false;
  }, []);

  return { sendMessage, sendAutoMessage };
}
